import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/PageHeader";
import { LeadFilters } from "@/components/leads/LeadFilters";
import { LeadsTable } from "@/components/leads/LeadsTable";
import { LeadDetailSheet } from "@/components/leads/LeadDetailSheet";
import { api, downloadFile, formatApiError } from "@/lib/api";

const EMPTY = { q: "", city: "", source: "", exclude_dnc: false, only_dnc: false, has_email: false, has_website: false, has_leader: false, legal_status: "" };

const cleanParams = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v !== false && v != null));

export default function LeadsManager() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job_id") || "";
  const [filters, setFilters] = useState(EMPTY);
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [exporting, setExporting] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [debouncedQ, filters.city, filters.source, filters.exclude_dnc, filters.only_dnc, filters.has_email, filters.has_website, filters.has_leader, filters.legal_status, jobId, pageSize]);

  const query = cleanParams({ ...filters, q: debouncedQ, job_id: jobId });
  const { data, isFetching } = useQuery({
    queryKey: ["leads", query, page, pageSize],
    queryFn: () => api.get("/leads", { params: { ...query, page, page_size: pageSize } }).then((r) => r.data),
    placeholderData: keepPreviousData,
    refetchInterval: 6000,
  });

  const exportAs = async (format) => {
    setExporting(format);
    try {
      await downloadFile("/leads/export", { ...query, format }, `leads-b2b.${format}`);
      toast.success(`Export ${format.toUpperCase()} généré`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting("");
    }
  };

  const bulkDelete = async () => {
    try {
      const { data: res } = await api.post("/leads/bulk-delete", { ids: selected });
      toast.success(`${res.deleted} lead(s) supprimé(s)`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const actions = (
    <>
      {selected.length > 0 && (
        <Button variant="outline" data-testid="bulk-delete-button" onClick={bulkDelete} className="text-[#FF3B30] hover:text-[#FF3B30]">
          <Trash2 className="mr-2 h-4 w-4" /> Supprimer ({selected.length})
        </Button>
      )}
      <Button variant="outline" data-testid="export-csv-button" onClick={() => exportAs("csv")} disabled={!!exporting}>
        {exporting === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} Exporter en CSV
      </Button>
      <Button data-testid="export-xlsx-button" onClick={() => exportAs("xlsx")} disabled={!!exporting} className="transition-transform hover:-translate-y-0.5">
        {exporting === "xlsx" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Exporter en Excel (.xlsx)
      </Button>
    </>
  );

  return (
    <div data-testid="leads-page">
      <PageHeader index="03" title="Base de données Leads" subtitle="Leads dédoublonnés multi-sources. Les exports respectent les filtres actifs." actions={actions} />
      <LeadFilters
        filters={filters}
        setFilters={setFilters}
        jobId={jobId}
        clearJob={() => setParams({})}
        reset={() => setFilters(EMPTY)}
      />
      <LeadsTable
        data={data}
        loading={isFetching}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        selected={selected}
        setSelected={setSelected}
        onOpen={setOpenId}
      />
      <LeadDetailSheet leadId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
